import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2, TreePine } from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SignupPage = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');
  
  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [validatingToken, setValidatingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  const { signup } = useAuth();
  const navigate = useNavigate();

  // Validate invite token on mount
  useEffect(() => {
    const validateToken = async () => {
      if (!inviteToken) {
        setValidatingToken(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/api/invite/validate/${inviteToken}`);
        setFormData(prev => ({
          ...prev,
          email: response.data.email,
          firstName: response.data.first_name,
          lastName: response.data.last_name
        }));
        setTokenValid(true);
      } catch (err) {
        setError(err.response?.data?.detail || 'Invalid or expired invitation link');
      } finally {
        setValidatingToken(false);
      }
    };

    validateToken();
  }, [inviteToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return { minLength, hasUpper, hasLower, hasNumber, hasSpecial };
  };

  const passwordChecks = validatePassword(formData.password);
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!isPasswordValid) {
      setError('Password does not meet requirements');
      return;
    }

    setLoading(true);

    try {
      const userData = await signup({
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        phone: formData.phone || null,
        invite_token: inviteToken
      });
      
      // Redirect based on user role
      const userRole = userData?.role;
      if (userRole === 'client') {
        navigate('/client');
      } else if (userRole === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  if (validatingToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-[#1D2D2A]" />
          <p className="text-[#6C757D]">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (!inviteToken || !tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA] p-4">
        <Card className="w-full max-w-md border-0 shadow-lg">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img 
                src="/logo-tree.svg" 
                alt="Plane Tree Capital" 
                className="w-12 h-12"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-[#212529] font-['Manrope']">
              Invitation Required
            </CardTitle>
            <CardDescription className="text-[#6C757D]">
              {error || 'You need a valid invitation link to create an account.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="bg-amber-50 border-amber-200 mb-6">
              <AlertCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                Access to the Plane Tree Capital Investor Portal is by invitation only. 
                Please contact your relationship manager if you need access.
              </AlertDescription>
            </Alert>
            <Link to="/login">
              <Button
                className="w-full h-11 bg-[#1D2D2A] hover:bg-[#243D37] text-white"
                data-testid="back-to-login-button"
              >
                Return to Login
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branded panel */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-[#1D2D2A]">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23BC9D6E\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")'}}></div>
        
        <div className="relative z-10 w-full flex flex-col justify-center items-center p-12">
          <div className="max-w-md text-center">
            {/* Logo */}
            <img 
              src="/logo-tree.svg" 
              alt="Plane Tree Capital" 
              className="h-32 mx-auto mb-8"
            />
            
            {/* Title */}
            <h1 className="text-3xl font-bold text-[#DFD5BC] font-['Manrope'] mb-6">
              Plane Tree Capital Portal
            </h1>
            
            {/* Decorative line */}
            <div className="flex items-center justify-center gap-4">
              <div className="h-px w-16 bg-[#BC9D6E]/40"></div>
              <div className="w-2 h-2 rounded-full bg-[#BC9D6E]"></div>
              <div className="h-px w-16 bg-[#BC9D6E]/40"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-md animate-fade-in">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center mb-8">
            <img 
              src="/logo-horizontal.svg" 
              alt="Plane Tree Capital" 
              className="h-12"
            />
          </div>

          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold text-[#212529] font-['Manrope']">
                Create your account
              </CardTitle>
              <CardDescription className="text-[#6C757D]">
                Complete your registration to access the investor portal
              </CardDescription>
            </CardHeader>
            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-6 bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-[#212529] font-medium">
                      First Name
                    </Label>
                    <Input
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      readOnly
                      data-testid="signup-firstname-input"
                      className="h-10 bg-gray-50 border-gray-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-[#212529] font-medium">
                      Last Name
                    </Label>
                    <Input
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      readOnly
                      data-testid="signup-lastname-input"
                      className="h-10 bg-gray-50 border-gray-200"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#212529] font-medium">
                    Email address
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    readOnly
                    data-testid="signup-email-input"
                    className="h-10 bg-gray-50 border-gray-200"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-[#212529] font-medium">
                    Phone Number <span className="text-[#ADB5BD]">(optional)</span>
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+27 XX XXX XXXX"
                    value={formData.phone}
                    onChange={handleChange}
                    data-testid="signup-phone-input"
                    className="h-10 border-gray-200 focus:border-[#1D2D2A] focus:ring-[#1D2D2A]"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#212529] font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create a strong password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      data-testid="signup-password-input"
                      className="h-10 pr-10 border-gray-200 focus:border-[#1D2D2A] focus:ring-[#1D2D2A]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  
                  {/* Password requirements */}
                  {formData.password && (
                    <div className="mt-2 space-y-1">
                      {[
                        { key: 'minLength', label: 'At least 8 characters' },
                        { key: 'hasUpper', label: 'One uppercase letter' },
                        { key: 'hasLower', label: 'One lowercase letter' },
                        { key: 'hasNumber', label: 'One number' },
                        { key: 'hasSpecial', label: 'One special character' }
                      ].map(({ key, label }) => (
                        <div key={key} className="flex items-center gap-2 text-xs">
                          <CheckCircle2 
                            className={`h-3.5 w-3.5 ${passwordChecks[key] ? 'text-[#198754]' : 'text-[#ADB5BD]'}`} 
                          />
                          <span className={passwordChecks[key] ? 'text-[#198754]' : 'text-[#6C757D]'}>
                            {label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-[#212529] font-medium">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    data-testid="signup-confirm-password-input"
                    className="h-10 border-gray-200 focus:border-[#1D2D2A] focus:ring-[#1D2D2A]"
                  />
                  {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading || !isPasswordValid || formData.password !== formData.confirmPassword}
                  data-testid="signup-submit-button"
                  className="w-full h-11 mt-2 bg-[#1D2D2A] hover:bg-[#243D37] text-white font-medium transition-all duration-200 active:scale-[0.98]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-[#6C757D]">
                  Already have an account?{' '}
                  <Link 
                    to="/login" 
                    className="text-[#18A37C] hover:text-[#1D2D2A] font-medium transition-colors"
                    data-testid="login-link"
                  >
                    Sign in
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>

          <p className="mt-8 text-center text-xs text-[#ADB5BD]">
            By creating an account, you agree to our Terms of Service and Privacy Policy.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
